import {
  plannerCurrencyMinorUnitDigits,
  plannerPurseMinorToAmount,
} from "@hisani/planner";
import type { useTranslations } from "next-intl";
import type { PlannerLocale, PlannerRace } from "./planner-contracts";

type PlannerTranslations = ReturnType<typeof useTranslations<"Planner">>;

export function plannerDateLabel(value: string, locale: PlannerLocale): string {
  const [year, month, day] = value.split("-");
  const formatted = `${day}/${month}/${year}`;
  return locale === "ar" ? localizePlannerDigits(formatted) : formatted;
}

export function localizePlannerDigits(value: string | number): string {
  return String(value).replace(
    /\d/g,
    (digit) => "٠١٢٣٤٥٦٧٨٩"[Number(digit)] ?? digit,
  );
}

export function plannerSurfaceLabel(
  surface: PlannerRace["surface"],
  t: PlannerTranslations,
): string {
  if (surface === null) return t("notAvailable");
  return surface === "DIRT"
    ? t("dirt")
    : surface === "TURF"
      ? t("turf")
      : t("allWeather");
}

export function plannerBreedLabel(
  breed: PlannerRace["breed"],
  t: PlannerTranslations,
): string {
  return breed === "THOROUGHBRED"
    ? t("thoroughbred")
    : breed === "PUREBRED_ARABIAN"
      ? t("purebredArabian")
      : breed === "OPEN"
        ? t("open")
        : t("other");
}

export function plannerPurseFormat(race: PlannerRace): {
  readonly amount: `${number}`;
  readonly fractionDigits: 0 | 2 | 3;
} | null {
  return plannerPurseFormatValues(race.purseMinor, race.currency);
}

export function plannerPurseAmountLabel(
  purseMinor: string | null,
  currency: string | null,
  locale: PlannerLocale,
): string | null {
  const purse = plannerPurseFormatValues(purseMinor, currency);
  if (purse === null) return null;
  return new Intl.NumberFormat(plannerNumberLocale(locale), {
    minimumFractionDigits: purse.fractionDigits,
    maximumFractionDigits: purse.fractionDigits,
  }).format(purse.amount);
}

function plannerPurseFormatValues(
  purseMinor: string | null,
  currency: string | null,
): {
  readonly amount: `${number}`;
  readonly fractionDigits: 0 | 2 | 3;
} | null {
  if (purseMinor === null || currency === null) return null;
  const amount = plannerPurseMinorToAmount(purseMinor, currency);
  const fractionDigits = plannerCurrencyMinorUnitDigits(currency);
  if (
    amount === null ||
    fractionDigits === null ||
    isDecimalAmount(amount) === false
  )
    return null;
  return { amount, fractionDigits };
}

export function plannerPurseLabel(
  race: PlannerRace,
  locale: PlannerLocale,
): string {
  const currency = race.currency;
  const purse = plannerPurseFormat(race);
  if (purse === null) {
    return currency === null || race.purseMinor === null
      ? "—"
      : `${currency} ${race.purseMinor}`;
  }
  if (currency === null) return "—";
  return new Intl.NumberFormat(plannerNumberLocale(locale), {
    style: "currency",
    currency,
    minimumFractionDigits: purse.fractionDigits,
    maximumFractionDigits: purse.fractionDigits,
  }).format(purse.amount);
}

export function plannerCompactPurseAmountLabel(
  race: PlannerRace,
  locale: PlannerLocale,
): string {
  const purse = plannerPurseFormat(race);
  if (purse === null) return race.purseMinor ?? "—";
  const amount = Number(purse.amount);
  if (Number.isFinite(amount) === false) return race.purseMinor ?? "—";
  return new Intl.NumberFormat(plannerNumberLocale(locale), {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(amount);
}

export function plannerCurrencySymbol(
  currency: string | null,
  locale: PlannerLocale,
): string {
  if (currency === null) return "";
  try {
    return (
      new Intl.NumberFormat(plannerNumberLocale(locale), {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
      })
        .formatToParts(0)
        .find((part) => part.type === "currency")?.value ?? currency
    );
  } catch {
    return currency;
  }
}

function isDecimalAmount(value: string): value is `${number}` {
  return /^\d+(?:\.\d+)?$/u.test(value);
}

function plannerNumberLocale(locale: PlannerLocale): string {
  return locale === "ar" ? "ar-u-nu-arab" : locale;
}

export function plannerRaceClipboardText(
  race: PlannerRace,
  locale: PlannerLocale,
  t: PlannerTranslations,
): string {
  const course =
    locale === "ar" ? race.racecourse.nameAr : race.racecourse.name;
  const distance =
    race.distanceMeters === null
      ? t("notAvailable")
      : `${race.distanceMeters.toLocaleString(locale)} ${t("metres")}`;
  return [
    `${course} · ${race.raceDate}`,
    `${race.raceName ?? race.grade ?? t("notAvailable")} · ${distance} · ${plannerSurfaceLabel(race.surface, t)}`,
    `${plannerBreedLabel(race.breed, t)} · ${race.season}`,
    plannerPurseLabel(race, locale),
  ].join("\n");
}
